import { animate } from "motion";
import { spring } from "motion";

const siteHeader = document.getElementById("site-header");

const mobileMenuToggle = document.querySelector<HTMLButtonElement>(
  "[aria-controls*='mobile-menu'"
);

const mobileSearchToggle = document.querySelector<HTMLButtonElement>(
  "[aria-controls*='mobile-search'"
);

const mobileFlyout = document.getElementById("mobile-flyout");
const mobileMenu = document.getElementById("mobile-menu");
const mobileMenuRootPanel: HTMLElement | null | undefined = mobileFlyout?.querySelector(
  "[data-mobile-nav-level-root]"
);
const nextButtons = mobileFlyout?.querySelectorAll("[data-mobile-nav-next]");
const backButtons = mobileFlyout?.querySelectorAll("[data-mobile-nav-back]");

const mobileSearch = document.getElementById("mobile-search");
const bannersWrapper = document.getElementById("banners-wrapper");

let flyoutIsOpen = false;
let menuIsOpen = false;
let searchIsOpen = false;

if (!mobileFlyout || !mobileMenu) {
  console.warn("Mobile menu is missing");
} else {
  /**
   * Toggles the visibility of the mobile flyout.
   * @param targetState (Optional) Force the flyout to open or close, regardless of its current state.
   */
  const toggleFlyout = (targetState?: boolean) => {
    const shouldExpand =
      targetState !== undefined
        ? targetState
        : mobileFlyout.getAttribute("aria-hidden") !== "false";

    mobileFlyout.setAttribute("aria-hidden", String(!shouldExpand));

    if (flyoutIsOpen !== shouldExpand) {
      siteHeader?.dispatchEvent(new Event(shouldExpand ? "flyout:open" : "flyout:close"));
    }

    flyoutIsOpen = shouldExpand;
  };

  /**
   * Opens or closes the mobile menu.
   * @param targetState (Optional) Force the menu to open or close, regardless of its current state.
   * @param keepOpen If true, the flyout will remain open even if the menu itself is toggled off.
   */
  const toggleMenu = (targetState?: boolean, keepOpen?: boolean) => {
    const shouldOpen =
      targetState !== undefined
        ? targetState
        : mobileMenu.getAttribute("aria-hidden") === "true";

    toggleFlyout(keepOpen || shouldOpen);

    mobileMenu.setAttribute("aria-hidden", String(!shouldOpen));

    if (mobileMenuToggle) {
      mobileMenuToggle.setAttribute("aria-expanded", String(shouldOpen));

      const lines = [...mobileMenuToggle.querySelectorAll<HTMLElement>("span")];

      if (shouldOpen) {
        animate(lines[0], { y: "0.34rem", rotate: "45deg" }, { type: spring });
        animate(lines[1], { x: "1.6rem", opacity: 0 }, { type: spring });
        animate(lines[2], { y: "-0.34rem", rotate: "-45deg" }, { type: spring });
      } else {
        animate(lines[0], { y: 0, rotate: 0 }, { y: { delay: 0.2 }, type: spring });
        animate(lines[1], { x: 0, opacity: 1 }, { delay: 0.2, type: spring });
        animate(lines[2], { y: 0, rotate: 0 }, { y: { delay: 0.2 }, type: spring });
      }
    }

    if (searchIsOpen) {
      toggleSearch(false, true);
    }

    menuIsOpen = shouldOpen;

    document.documentElement.classList.toggle("max-lg:overflow-y-hidden", shouldOpen);

    // Hide/show banners wrapper when burger menu opens/closes
    if (bannersWrapper) {
      if (shouldOpen) {
        bannersWrapper.style.transition = "height 0.3s ease-in-out";
        bannersWrapper.style.height = "0";
        bannersWrapper.style.overflow = "hidden";
      } else {
        bannersWrapper.style.transition = "height 0.3s ease-in-out";
        bannersWrapper.style.height = "";
        bannersWrapper.style.overflow = "";
      }
    }
  };

  /**
   * Opens or closes the mobile search form.
   * @param targetState (Optional) Force the search form to open or close, regardless of its current state.
   * @param keepOpen If true, the flyout will remain open even if the search form itself is toggled off.
   */
  const toggleSearch = (targetState?: boolean, keepOpen?: boolean) => {
    if (!mobileSearch) {
      return;
    }

    const shouldOpen =
      targetState !== undefined
        ? targetState
        : mobileSearch.getAttribute("aria-hidden") === "true";

    toggleFlyout(keepOpen || shouldOpen);

    mobileSearch.setAttribute("aria-hidden", String(!shouldOpen));
    mobileSearchToggle?.setAttribute("aria-expanded", String(shouldOpen));

    if (menuIsOpen) {
      toggleMenu(false, true);
    }

    searchIsOpen = shouldOpen;

    if (shouldOpen) {
      setTimeout(() => {
        mobileSearch.querySelector<HTMLInputElement>("input")?.focus();
      }, 200);
    }
  };

  // Each panel (i.e. sublevel/section in the navigation) is identified by the path it represents.
  // Use that identifier to open the correct chain of panels.
  const openPanelByPath = (path: string) => {
    if (!mobileMenuRootPanel) {
      return;
    }

    const closedClasses = ["opacity-0", "pointer-events-none", "touch-none", "invisible"];
    const panels = Array.from(
      mobileMenuRootPanel.querySelectorAll<HTMLElement>("[data-mobile-nav-panel-path]")
    );

    // Find the active panel (longest matching prefix)
    let activePanel: HTMLElement | null = null;
    let maxMatchLength = -1;
    panels.forEach(panel => {
      const panelPath = panel.dataset.mobileNavPanelPath ?? "";
      if (path.startsWith(panelPath) && panelPath.length > maxMatchLength) {
        maxMatchLength = panelPath.length;
        activePanel = panel;
      }
    });

    // Build the chain of active paths from root to the active panel
    const activePaths: string[] = [];
    let currentPanel: HTMLElement | null = activePanel;
    while (currentPanel) {
      const panelPath = (currentPanel as HTMLElement).dataset.mobileNavPanelPath ?? "";
      if (panelPath) {
        activePaths.unshift(panelPath);
      }
      // Find parent panel by traversing up the DOM tree
      currentPanel =
        (currentPanel as HTMLElement).parentElement?.closest<HTMLElement>(
          "[data-mobile-nav-panel-path]"
        ) ?? null;
    }

    // Show only panels in the active path chain
    panels.forEach(panel => {
      const panelPath = panel.dataset.mobileNavPanelPath ?? "";
      if (activePaths.includes(panelPath)) {
        panel.classList.remove(...closedClasses);
      } else {
        panel.classList.add(...closedClasses);
      }
    });

    // Set depth and left offset based on the number of panels in the chain
    const activeDepth = activePaths.length;
    mobileMenuRootPanel.setAttribute(
      "data-mobile-nav-current-index",
      String(activeDepth)
    );
    mobileMenuRootPanel.style.left = `${Math.max(0, activeDepth - 1) * -100}%`;
  };

  const goToNextLevel = (e: Event) => {
    const clickedButton = e.currentTarget as HTMLElement;
    const panel = clickedButton.nextElementSibling as HTMLElement | null;

    if (!panel || !panel.hasAttribute("data-mobile-nav-panel-path")) {
      return;
    }

    openPanelByPath(panel.dataset.mobileNavPanelPath ?? "");
  };

  const goBack = (e: Event) => {
    const clickedButton = e.currentTarget as HTMLElement;
    const panel = clickedButton.closest(
      "[data-mobile-nav-panel-path]"
    ) as HTMLElement | null;
    const parentPanel = panel?.parentElement?.closest(
      "[data-mobile-nav-panel-path]"
    ) as HTMLElement | null;

    if (!parentPanel) {
      return;
    }

    openPanelByPath(parentPanel.dataset.mobileNavPanelPath ?? "");
  };

  mobileMenuToggle?.addEventListener("click", () => toggleMenu());

  mobileSearchToggle?.addEventListener("click", () => toggleSearch());

  nextButtons?.forEach(button => button.addEventListener("click", goToNextLevel));
  backButtons?.forEach(button => button.addEventListener("click", goBack));

  // Make sure correct panel is open by default on page load
  openPanelByPath(window.location.pathname);
}
